import type { GestifyStatusResponse } from '@k-management/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useCurrentUser } from '../auth/AuthProvider.js';
import { apiRequest } from '../lib/api.js';
import { type GestifyTarget, useGestifyBrowser } from '../lib/gestify-browser.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { Field } from './Field.js';

/**
 * Which Gestify the browser reads: the settings of this environment, for the
 * administrators who may read them (ADR-011); otherwise the production one
 * and the company of the user.
 */
export function useGestifyTarget(): { target: GestifyTarget; username: string | null } {
  const user = useCurrentUser();
  const status = useQuery({
    queryKey: ['gestify'],
    queryFn: () => apiRequest<GestifyStatusResponse>('/gestify'),
    enabled: user.isAdmin,
  });
  return {
    target: {
      environment: status.data?.environment ?? 'production',
      companyId: status.data?.companyId ?? null,
    },
    username: status.data?.username ?? null,
  };
}

/**
 * «Collegati a Gestify» (ADR-017): the user's own Gestify account, and the
 * code of the authenticator app when Gestify asks for it. The password goes
 * from this browser to Gestify only.
 */
export function GestifyConnectDialog({
  onClose,
  onConnected,
}: {
  onClose: () => void;
  onConnected?: () => void;
}) {
  const gestify = useGestifyBrowser();
  const { target, username: suggested } = useGestifyTarget();
  const [username, setUsername] = useState(suggested ?? '');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [codeToken, setCodeToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // The user of this environment, once its settings arrive.
  useEffect(() => {
    if (suggested) setUsername((current) => current || suggested);
  }, [suggested]);

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      if (codeToken) {
        await gestify.verify(codeToken, code, target, username.trim());
      } else {
        const result = await gestify.connect(username.trim(), password, target);
        if (result.status === 'code') {
          setCodeToken(result.token);
          return;
        }
      }
      setPassword('');
      onConnected?.();
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ConfirmDialog
      title="Collegati a Gestify"
      confirmLabel={codeToken ? 'Verifica il codice' : 'Collegati'}
      busy={busy}
      error={error}
      onConfirm={() => void confirm()}
      onCancel={onClose}
    >
      {codeToken ? (
        <Field label="Codice di verifica" hint="Il codice a 6 cifre dell’app di autenticazione.">
          <input
            value={code}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            onChange={(event) => setCode(event.target.value)}
          />
        </Field>
      ) : (
        <>
          <Field label="Utente Gestify">
            <input
              value={username}
              autoComplete="username"
              onChange={(event) => setUsername(event.target.value)}
            />
          </Field>
          <Field label="Password">
            <input
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>
        </>
      )}
      <p className="muted small">
        La password va direttamente da questo browser a Gestify: K-Management non la riceve e non la
        salva. Il collegamento resta aperto finché non chiudi la scheda. Si legge soltanto, in
        Gestify non viene scritto nulla.
      </p>
    </ConfirmDialog>
  );
}
